import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, buttonType } from 'components/button/button';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { ReactComponent as LeftArray } from 'assets/icons/signin/left-array.svg';
import './code.scss';
import { onlyEnDigits, ToFa } from 'core/utils/decimalExtentions';
import { postGeneric, responseValidator } from 'apis/api';
import { API } from 'apis';
import { LoginResult } from '../login-with-password/login-with-passowrd';
import { useNavigate } from 'react-router-dom';
import OtpInput from 'react-otp-input';
import { fetchAndStoreMenuItems, persistSession } from 'core/utils/auth-session';

interface Props {}

const CODE_LENGTH = 6;
/** مدت اعتبار کد پیامکی بر حسب ثانیه (فرض پیش‌فرض؛ در صورت تفاوت با بک‌اند تنظیم شود). */
const CODE_TTL_SECONDS = 120;

function formatTimer(totalSeconds: number) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return ToFa(`${minutes}:${seconds.toString().padStart(2, '0')}`);
}

function maskPhone(phone: string) {
    if (phone.length !== 11) return ToFa(phone);
    return ToFa(`${phone.slice(0, 4)}***${phone.slice(7)}`);
}

export const Code: React.FC<Props> = () => {
    const [codeValue, setCodeValue] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [remaining, setRemaining] = useState<number>(CODE_TTL_SECONDS);

    const navigate = useNavigate();
    const phone = localStorage.getItem('phone') || '';
    /** جلوگیری از ارسال دوباره وقتی کد به صورت خودکار تکمیل می‌شود. */
    const submittedRef = useRef<string>('');

    // بدون شماره، صفحه کد معنایی ندارد
    useEffect(() => {
        if (!phone) navigate('/signin', { replace: true });
    }, [phone, navigate]);

    // شمارش معکوس اعتبار کد
    useEffect(() => {
        const id = window.setInterval(() => {
            setRemaining((s) => {
                if (s <= 1) window.clearInterval(id);
                return s > 0 ? s - 1 : 0;
            });
        }, 1000);
        return () => window.clearInterval(id);
    }, []);

    const confirmCode = useCallback(
        (code: string) => {
            if (code.length !== CODE_LENGTH || submittedRef.current === code) return;
            submittedRef.current = code;

            setLoading(true);
            setError('');
            postGeneric<LoginResult>(API.general.verify2fa, {
                platformType: 1,
                phoneNumber: phone,
                code,
            })
                .then(async (res) => {
                    if (!responseValidator(res.status) || !res.value) {
                        setLoading(false);
                        setError(res.message || 'کد وارد شده نادرست یا منقضی شده است.');
                        setCodeValue('');
                        submittedRef.current = '';
                        return;
                    }

                    persistSession(res.value);
                    await fetchAndStoreMenuItems();
                    setLoading(false);
                    navigate('/dashboard/users');
                })
                .catch(() => {
                    setLoading(false);
                    submittedRef.current = '';
                    navigate('/signin');
                });
        },
        [phone, navigate],
    );

    const onCodeChange = useCallback(
        (value: string) => {
            // ارقام فارسی/عربی (و چسباندن کد از پیامک) به ارقام انگلیسی تبدیل می‌شود.
            const digits = onlyEnDigits(value, CODE_LENGTH);
            setCodeValue(digits);
            setError((prev) => (prev ? '' : prev));
            if (digits.length === CODE_LENGTH) confirmCode(digits);
        },
        [confirmCode],
    );

    // تکمیل خودکار کد از پیامک روی اندروید/کروم (WebOTP)
    useEffect(() => {
        if (!('OTPCredential' in window) || !navigator.credentials) return;

        const controller = new AbortController();
        (navigator.credentials as any)
            .get({ otp: { transport: ['sms'] }, signal: controller.signal })
            .then((otp: any) => otp?.code && onCodeChange(otp.code))
            .catch(() => {
                /* کاربر رد کرد یا پیامکی نرسید — بی‌صدا نادیده گرفته می‌شود */
            });

        return () => controller.abort();
    }, [onCodeChange]);

    const expired = remaining <= 0;

    return (
        <div className="code-page">
            <button
                type="button"
                className="signin-back"
                onClick={() => navigate('/signin')}
                aria-label="بازگشت به صفحه ورود"
            >
                <LeftArray />
            </button>

            <div className="title">
                <h1>کد تایید دو مرحله‌ای</h1>
                <p className="subtitle">
                    کد ۶ رقمی ارسال‌شده به شماره <bdi className="phone">{maskPhone(phone)}</bdi> را وارد کنید.
                </p>
            </div>

            <div className="otp-wrapper">
                <OtpInput
                    value={codeValue}
                    onChange={onCodeChange}
                    numInputs={CODE_LENGTH}
                    shouldAutoFocus
                    inputType="tel"
                    inputStyle={`pmx-otp-input ${error ? 'has-error' : ''}`}
                    containerStyle={'pmx-otp-input-container'}
                    renderInput={(inputProps, index) => (
                        <input
                            {...inputProps}
                            // کتابخانه به صورت پیش‌فرض autoComplete="off" و width:1em می‌گذارد
                            autoComplete="one-time-code"
                            inputMode="numeric"
                            style={{ textAlign: 'center' }}
                            aria-label={`رقم ${index + 1} از ${CODE_LENGTH} کد تایید`}
                        />
                    )}
                />

                <div className="otp-status">
                    <span className="otp-error" role="alert">
                        {error && (
                            <>
                                <ExclamationCircleOutlined />
                                {error}
                            </>
                        )}
                    </span>

                    <span className={`otp-timer ${expired ? 'expired' : ''}`}>
                        {expired ? 'مهلت کد به پایان رسید' : `اعتبار کد: ${formatTimer(remaining)}`}
                    </span>
                </div>
            </div>

            {/* شمارنده فقط اطلاع‌رسانی است؛ اعتبارسنجی نهایی کد با سرور است و دکمه غیرفعال نمی‌شود. */}
            <Button
                disabled={codeValue.length !== CODE_LENGTH}
                type={buttonType.info}
                loading={loading}
                text="تایید و ورود"
                onClick={() => confirmCode(codeValue)}
            />

            <p className="footer-text">
                {expired ? 'کد را دریافت نکردید؟ ' : 'شماره وارد شده متعلق به شما نیست؟ '}
                <button type="button" onClick={() => navigate('/signin')}>
                    {expired ? 'ورود مجدد' : 'تصحیح شماره'}
                </button>
            </p>
        </div>
    );
};
